import React from "react";
import { X, Upload, FileText, AlertTriangle, CheckCircle2, Loader2, Trash2, Clipboard } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const REQUIRED = [
  "pickup_address", "pickup_zip", "pickup_city",
  "delivery_address", "delivery_zip", "delivery_city",
  "recipient_name", "recipient_phone",
  "package_count", "weight_kg", "delivery_type",
];

const ALL_HEADERS = [
  "pickup_address", "pickup_zip", "pickup_city",
  "delivery_address", "delivery_zip", "delivery_city",
  "recipient_name", "recipient_phone",
  "package_count", "weight_kg", "delivery_type",
  "pickup_date", "pickup_time", "notes", "delivery_instruction",
];

const TEMPLATE_CSV =
  ALL_HEADERS.join(",") + "\n" +
  "Vesterbrogade 10,1620,København V,Roskildevej 100,2000,Frederiksberg,Anders Andersen,+4530123456,1,2.5,same_day,2026-02-20,19:00,Ring v/ankomst,\n" +
  "Strøget 5,1200,København K,Hovedvejen 30,2600,Glostrup,Mette Mortensen,+4540987654,2,5,express,,,,Ved hoveddør\n";

// Robust CSV parser supporting quoted fields and commas inside values
function parseCSV(text) {
  const rows = [];
  let cur = "", row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (inQuotes) {
      if (c === '"' && n === '"') { cur += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else cur += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(cur); cur = ""; }
      else if (c === '\n') { row.push(cur); rows.push(row); row = []; cur = ""; }
      else if (c === '\r') { /* skip */ }
      else cur += c;
    }
  }
  if (cur.length || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((v) => v && v.trim()));
}

// Excel paste = TSV (tab separated). Detect & convert.
function detectAndParse(text) {
  const firstLine = text.split(/\r?\n/)[0] || "";
  const sep = (firstLine.includes("\t") && !firstLine.includes(",")) ? "\t" : "csv";
  if (sep === "\t") {
    return text.split(/\r?\n/).filter((l) => l.trim()).map((l) => l.split("\t"));
  }
  return parseCSV(text);
}

function rowsToObjects(grid) {
  if (!grid || grid.length < 2) return { headers: [], objects: [], errors: ["Mindst 1 header-række + 1 data-række kræves"] };
  const headers = grid[0].map((h) => (h || "").trim().toLowerCase().replace(/\s+/g, "_"));
  const errors = [];
  REQUIRED.forEach((req) => {
    if (!headers.includes(req)) errors.push(`Manglende kolonne: ${req}`);
  });
  const objects = grid.slice(1).map((r, idx) => {
    const obj = { _row: idx + 2 };
    headers.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
    return obj;
  });
  return { headers, objects, errors };
}

function validateRow(o) {
  const errs = [];
  REQUIRED.forEach((req) => {
    if (!o[req]) errs.push(req);
  });
  if (o.package_count && (isNaN(Number(o.package_count)) || Number(o.package_count) < 1)) errs.push("package_count (skal være ≥ 1)");
  if (o.weight_kg && (isNaN(Number(o.weight_kg)) || Number(o.weight_kg) <= 0)) errs.push("weight_kg (skal være > 0)");
  if (o.delivery_type && !["same_day", "express", "evening", "business"].includes(o.delivery_type)) errs.push(`delivery_type='${o.delivery_type}'`);
  return errs;
}

export default function BulkImportModal({ open, onClose, customers = [], defaultCustomerId = "", onComplete }) {
  const [customerId, setCustomerId] = React.useState(defaultCustomerId || "");
  const [pasteText, setPasteText] = React.useState("");
  const [parsed, setParsed] = React.useState({ headers: [], objects: [], errors: [] });
  const [submitting, setSubmitting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const fileRef = React.useRef(null);

  React.useEffect(() => { if (open) setCustomerId(defaultCustomerId || ""); }, [open, defaultCustomerId]);

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setPasteText(text);
    setParsed(rowsToObjects(detectAndParse(text)));
    setResult(null);
  };

  const handlePaste = (val) => {
    setPasteText(val);
    if (val.trim()) setParsed(rowsToObjects(detectAndParse(val)));
    else setParsed({ headers: [], objects: [], errors: [] });
    setResult(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "ema-bulk-bookings-template.csv";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const submit = async () => {
    if (!customerId) { toast.error("Vælg en erhvervskunde"); return; }
    if (!parsed.objects.length) { toast.error("Ingen rækker at importere"); return; }
    if (parsed.errors.length) { toast.error("Ret kolonne-fejl først"); return; }

    const validRows = [];
    const skipped = [];
    parsed.objects.forEach((o) => {
      const errs = validateRow(o);
      if (errs.length) { skipped.push({ line: o._row, reason: `Manglende/ugyldigt: ${errs.join(", ")}` }); return; }
      validRows.push({
        pickup_address: o.pickup_address,
        pickup_zip: o.pickup_zip,
        pickup_city: o.pickup_city,
        delivery_address: o.delivery_address,
        delivery_zip: o.delivery_zip,
        delivery_city: o.delivery_city,
        recipient_name: o.recipient_name,
        recipient_phone: o.recipient_phone,
        package_count: Number(o.package_count),
        weight_kg: Number(o.weight_kg),
        delivery_type: o.delivery_type,
        pickup_date: o.pickup_date || null,
        pickup_time: o.pickup_time || "19:00",
        notes: o.notes || null,
        delivery_instruction: o.delivery_instruction || null,
      });
    });

    if (!validRows.length) { toast.error("Alle rækker har valideringsfejl"); return; }

    setSubmitting(true);
    try {
      const { data } = await adminApi().post(`/admin/business-customers/${customerId}/bulk-bookings`, {
        customer_id: customerId,
        rows: validRows,
        auto_paid: true,
      });
      setResult({ ...data, client_skipped: skipped });
      toast.success(`${data.created_count} bookings oprettet · ${(data.failed_count + skipped.length)} fejlet`);
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-import fejlede");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;
  const cnt = parsed.objects.length;
  const hasErrors = parsed.errors.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-testid="bulk-import-modal">
      <div className="bg-white border border-brand max-w-5xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-brand-dark text-white px-6 py-5 flex items-center justify-between">
          <div>
            <div className="kicker text-brand-red">Bulk-import</div>
            <h2 className="font-display font-bold text-2xl mt-1">Upload mange bookings på én gang</h2>
            <p className="text-white/60 text-sm mt-1">CSV eller indsæt direkte fra Excel — ubegrænset antal rækker (max 500 per import).</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="bulk-close"><X size={22} /></button>
        </div>

        <div className="p-6 sm:p-8 space-y-6">
          {/* Customer selection */}
          <div>
            <label className="label-flat">Erhvervskunde</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="input-flat" data-testid="bulk-customer">
              <option value="">— Vælg kunde —</option>
              {customers.filter((c) => c.active !== false).map((c) => (
                <option key={c.id} value={c.id}>{c.company_name} ({c.billing_email})</option>
              ))}
            </select>
          </div>

          {/* Template download + drop zone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-2 border-dashed border-brand-text2/30 hover:border-brand-red/60 transition p-6 text-center cursor-pointer"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-brand-red", "bg-brand-bg2"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("border-brand-red", "bg-brand-bg2")}
              onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-brand-red", "bg-brand-bg2"); handleFile(e.dataTransfer.files[0]); }}
              data-testid="bulk-dropzone"
            >
              <Upload size={28} className="mx-auto text-brand-red" />
              <div className="font-display font-semibold text-brand-dark mt-2">Træk CSV-fil hertil</div>
              <div className="text-xs text-brand-text2 mt-1">eller klik for at vælge</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleFile(e.target.files[0])} data-testid="bulk-file-input" />
            </div>
            <div className="border border-brand p-5">
              <div className="flex items-start gap-3">
                <FileText size={20} className="text-brand-red flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-display font-semibold text-brand-dark">Hent skabelon</div>
                  <p className="text-xs text-brand-text2 mt-1">Excel-kompatibel CSV med alle kolonner og 2 eksempel-rækker. Kopier dine egne data ind.</p>
                  <button onClick={downloadTemplate} className="btn-secondary mt-3 text-sm" data-testid="bulk-download-template">Download CSV-skabelon</button>
                </div>
              </div>
              <div className="text-xs text-brand-text2 mt-4 leading-relaxed">
                <strong className="text-brand-dark">Påkrævede kolonner:</strong> pickup_address, pickup_zip, pickup_city, delivery_address, delivery_zip, delivery_city, recipient_name, recipient_phone, package_count, weight_kg, delivery_type
              </div>
            </div>
          </div>

          {/* Paste from Excel */}
          <div>
            <label className="label-flat flex items-center gap-2"><Clipboard size={12} /> Eller indsæt fra Excel (Ctrl+V)</label>
            <textarea
              value={pasteText}
              onChange={(e) => handlePaste(e.target.value)}
              placeholder="Kopier celler i Excel/Google Sheets og indsæt her — eller paste CSV-tekst"
              rows={6}
              className="input-flat font-mono text-xs"
              data-testid="bulk-paste"
            />
          </div>

          {/* Parse result / preview */}
          {parsed.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-900 p-4 text-sm" data-testid="bulk-header-errors">
              <div className="font-semibold flex items-center gap-2"><AlertTriangle size={16} /> Header-fejl</div>
              <ul className="mt-2 list-disc list-inside">
                {parsed.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </div>
          )}

          {cnt > 0 && !hasErrors && (
            <div className="border border-brand" data-testid="bulk-preview">
              <div className="bg-brand-bg2 border-b border-brand px-4 py-3 flex items-center justify-between">
                <div className="font-display font-semibold text-brand-dark">Forhåndsvisning · {cnt} række{cnt !== 1 ? "r" : ""}</div>
                <button onClick={() => { setPasteText(""); setParsed({ headers: [], objects: [], errors: [] }); setResult(null); }} className="text-xs text-brand-text2 hover:text-red-700 flex items-center gap-1" data-testid="bulk-clear">
                  <Trash2 size={12} /> Ryd
                </button>
              </div>
              <div className="max-h-60 overflow-auto text-xs">
                <table className="w-full">
                  <thead className="bg-white border-b border-brand sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-left">#</th>
                      {ALL_HEADERS.map((h) => <th key={h} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>)}
                      <th className="px-2 py-2 text-left">Validering</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.objects.slice(0, 50).map((o) => {
                      const errs = validateRow(o);
                      return (
                        <tr key={o._row} className={`border-b border-brand last:border-0 ${errs.length ? "bg-red-50" : ""}`}>
                          <td className="px-2 py-1.5 font-mono text-brand-text2">{o._row}</td>
                          {ALL_HEADERS.map((h) => <td key={h} className="px-2 py-1.5 whitespace-nowrap max-w-[140px] truncate" title={o[h]}>{o[h] || "—"}</td>)}
                          <td className="px-2 py-1.5">
                            {errs.length ? (
                              <span className="text-red-700 text-[10px]">{errs.join(", ")}</span>
                            ) : (
                              <CheckCircle2 size={12} className="text-emerald-600" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {cnt > 50 && <div className="text-center text-brand-text2 text-xs py-2">+ {cnt - 50} flere rækker (vises ikke i preview)</div>}
              </div>
            </div>
          )}

          {/* Submit */}
          {!result && (
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="btn-secondary" data-testid="bulk-cancel">Annuller</button>
              <button onClick={submit} disabled={submitting || !customerId || !cnt || hasErrors} className="btn-primary disabled:opacity-50" data-testid="bulk-submit">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Importerer...</> : <>Importér {cnt} bookings</>}
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4" data-testid="bulk-result">
              <div className="bg-emerald-50 border border-emerald-200 p-5">
                <div className="font-display font-bold text-2xl text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 size={24} /> {result.created_count} bookings oprettet
                </div>
                <div className="text-emerald-800 text-sm mt-1">Total: {Number(result.total_dkk).toLocaleString("da-DK", { minimumFractionDigits: 2 })} kr</div>
              </div>
              {(result.failed_count > 0 || (result.client_skipped || []).length > 0) && (
                <div className="bg-amber-50 border border-amber-200 p-5">
                  <div className="font-semibold text-amber-900 flex items-center gap-2"><AlertTriangle size={16} /> {result.failed_count + (result.client_skipped || []).length} række{result.failed_count + (result.client_skipped || []).length !== 1 ? "r" : ""} fejlet</div>
                  <ul className="text-amber-900 text-sm mt-2 max-h-40 overflow-auto">
                    {(result.client_skipped || []).map((f, i) => <li key={`c${i}`}>· Linje {f.line}: {f.reason}</li>)}
                    {(result.failed || []).map((f, i) => <li key={`s${i}`}>· Linje {f.line}: {f.reason}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button onClick={() => { setResult(null); setPasteText(""); setParsed({ headers: [], objects: [], errors: [] }); }} className="btn-secondary" data-testid="bulk-import-more">Importér flere</button>
                <button onClick={onClose} className="btn-primary" data-testid="bulk-done">Færdig</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
